import React, { useMemo, useState } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate, formatCurrency } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, SummaryNote, Badge, Button
} from '../../components/ui';
import { sundaySchoolSummary, attendanceTrend } from './analytics';
import type { SsSummaryInput, SsStudentLike } from './analytics';
import { LOW_ATTENDANCE_PCT } from './registers';

export interface SundaySchoolSummaryProps extends SsSummaryInput {
  /** "2026-27" or "All years" — what the figures are scoped to. */
  scopeName: string;
  /**
   * Students in no live section. Left out for a teacher, who sees only their
   * own sections and has no say over enrolment.
   */
  unassigned?: SsStudentLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

/** The Sunday School's Summary tab: tiles, then report panels and watch-lists. */
const SundaySchoolSummaryView = ({ scopeName, unassigned, loading, isDarkMode, ...input }: SundaySchoolSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const { sections, students, teachers, registers, offerings, exams, marks, term } = input;
  const s = useMemo(
    () => sundaySchoolSummary({ sections, students, teachers, registers, offerings, exams, marks, term }),
    [sections, students, teachers, registers, offerings, exams, marks, term]
  );
  const [showAllStudents, setShowAllStudents] = useState(false);
  const sub = { fontSize: '12px', color: t.textFaint };
  const trend = attendanceTrend(s.last4, s.prev4);
  const offeringPeak = peakOf(s.offeringMonths, (r) => r.total);
  const pctColor = (pct: number | null) => (pct === null ? t.textFaint : pct >= 80 ? t.income : pct >= LOW_ATTENDANCE_PCT ? t.warning : t.danger);
  const pctBadge = (pct: number | null) => pct === null
    ? <span style={sub}>—</span>
    : <Badge tone={pct >= 80 ? 'success' : pct >= LOW_ATTENDANCE_PCT ? 'warning' : 'error'}>{pct.toFixed(1)}%</Badge>;
  const pctText = (pct: number | null) => (pct === null ? '—' : `${pct.toFixed(1)}%`);

  return (
    <>
      <SummaryNote isDarkMode={isDarkMode}>
        Figures for {scopeName}. Attendance counts heads present of heads marked; a blank mark is absent, not a fail.
      </SummaryNote>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Students Enrolled" value={s.students.toLocaleString('en-IN')} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Sections" value={`${s.sections} · ${s.teachers} teachers`} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Sections Without Teacher" value={String(s.sectionsWithoutTeacher.length)} color={s.sectionsWithoutTeacher.length ? t.danger : t.income} isDarkMode={isDarkMode} />
        <KpiCard
          label="Attendance (Last 4 Sundays)"
          value={<>{pctText(s.last4.pct)}{trend !== null && <span style={{ fontSize: '13px', marginLeft: '8px', color: trend >= 0 ? t.income : t.danger }}>{trend >= 0 ? '▲' : '▼'} {Math.abs(trend)} pts</span>}</>}
          color={pctColor(s.last4.pct)}
          isDarkMode={isDarkMode}
        />
        <KpiCard label={`Below ${LOW_ATTENDANCE_PCT}% Attendance`} value={String(s.lowAttendance.length)} color={s.lowAttendance.length ? t.danger : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Offerings This Month" value={formatCurrency(s.offeringsThisMonth)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label={`Offerings ${s.termLabel}`} value={formatCurrency(s.offeringsTerm)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Exam Pass Rate" value={pctText(s.examPassPct)} color={s.examPassPct !== null && s.examPassPct < 50 ? t.danger : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Exam Average" value={pctText(s.examAvgPct)} color={t.info} isDarkMode={isDarkMode} />
        {unassigned && (
          <KpiCard label="Without a Section" value={String(unassigned.length)} color={unassigned.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        )}
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Enrolment by Grade"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Grade', render: (r) => <><strong>{r.name}</strong><br /><span style={sub}>{r.sections} section{r.sections === 1 ? '' : 's'}{r.withoutTeacher ? ` · ${r.withoutTeacher} without teacher` : ''}</span></> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.students, s.students)} max={100} showPercent color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'students', header: 'Students', align: 'right' }
          ]}
          rows={s.byGrade}
          rowKey="name"
          emptyMessage={`No sections in ${scopeName}.`}
          below={s.byGrade.length ? <TotalRow label="Total enrolled" value={s.students} isDarkMode={isDarkMode} /> : null}
        />
        <TablePanel
          title="Attendance by Month"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
            { key: 'bar', header: 'Present', render: (r) => (r.pct === null ? <span style={sub}>—</span> : <BarCell value={r.pct} max={100} showPercent color={pctColor(r.pct)} isDarkMode={isDarkMode} />) },
            { key: 'count', header: 'Sheets', align: 'right', render: (r) => <>{r.count}{r.marked > 0 && <><br /><span style={sub}>{r.present} of {r.marked}</span></>}</> }
          ]}
          rows={s.attendanceMonths}
          rowKey="month"
          emptyMessage="No attendance marked yet."
          below={s.overall.pct !== null ? <TotalRow label="All Sundays" value={pctText(s.overall.pct)} color={pctColor(s.overall.pct)} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <TablePanel
        title={<>By Section — {scopeName}</>}
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="760px"
        columns={[
          { key: 'label', header: 'Section', render: (r) => <><strong>{r.label}</strong><br /><span style={sub}>{r.teachers || 'No teacher'}</span></> },
          { key: 'students', header: 'Students', align: 'center' },
          { key: 'registers', header: 'Sundays Marked', align: 'center', render: (r) => <>{r.registers}{r.lastRegister && <><br /><span style={sub}>last {formatDate(r.lastRegister)}</span></>}</> },
          { key: 'attendance', header: 'Attendance', align: 'center', render: (r) => <>{pctBadge(r.attendance.pct)}<br /><span style={sub}>{r.attendance.present} of {r.attendance.marked}</span></> },
          { key: 'offerings', header: 'Offerings', align: 'right', nowrap: true, render: (r) => <span style={{ color: t.income, fontWeight: 'bold' }}>{formatCurrency(r.offerings)}</span> },
          { key: 'results', header: 'Exam Pass', align: 'center', render: (r) => r.results.passPct === null ? <span style={sub}>—</span> : <>{pctBadge(r.results.passPct)}<br /><span style={sub}>{r.results.passed} of {r.results.sat}</span></> },
          { key: 'avgPct', header: 'Exam Avg', align: 'center', render: (r) => pctText(r.avgPct) }
        ]}
        rows={s.bySection}
        rowKey="id"
        emptyMessage={`No sections in ${scopeName}.`}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Offerings by Month"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={offeringPeak} color={t.income} isDarkMode={isDarkMode} /> },
            { key: 'total', header: 'Collected', align: 'right', nowrap: true, render: (r) => <span style={{ color: t.income, fontWeight: 'bold' }}>{formatCurrency(r.total)}</span> }
          ]}
          rows={s.offeringMonths}
          rowKey="month"
          emptyMessage="No offerings recorded yet."
          below={offerings.length ? (
            <>
              <TotalRow label="12 months" value={formatCurrency(s.offeringMonths.reduce((n, m) => n + m.total, 0))} color={t.income} isDarkMode={isDarkMode} />
              <div style={{ ...sub, marginTop: '6px' }}>
                All time {formatCurrency(s.offeringsTotal)} — section totals {formatCurrency(s.classTotals)}, children&apos;s own gifts {formatCurrency(s.personalGifts)}
              </div>
            </>
          ) : null}
        />
        <TablePanel
          title="Exam Results"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Exam', render: (r) => <><strong>{r.name}</strong><br /><span style={sub}>{r.date ? formatDate(r.date) : '-'} · marks in {r.entered} of {r.sections}</span></> },
            { key: 'passPct', header: 'Passed', render: (r) => (r.passPct === null ? <span style={sub}>—</span> : <BarCell value={r.passPct} max={100} showPercent color={r.passPct >= 50 ? t.income : t.danger} isDarkMode={isDarkMode} />) },
            { key: 'sat', header: 'Sat', align: 'right', render: (r) => <>{r.passed} of {r.sat}{r.absent > 0 && <><br /><span style={sub}>{r.absent} absent</span></>}</> },
            { key: 'avgPct', header: 'Avg', align: 'right', render: (r) => pctText(r.avgPct) }
          ]}
          rows={s.exams}
          rowKey="id"
          emptyMessage="No exams scheduled yet."
          below={s.examSat > 0 ? <TotalRow label="All exams" value={`${s.examPassed} of ${s.examSat} passed · ${pctText(s.examPassPct)}`} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Top Scorers"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Student', render: (r) => <><strong>{r.name}</strong><br /><span style={sub}>{r.id} · {r.className}</span></> },
            { key: 'exams', header: 'Exams', align: 'center' },
            { key: 'pct', header: 'Overall', align: 'right', render: (r) => <><Badge tone="success">{r.pct.toFixed(1)}%</Badge><br /><span style={sub}>{r.obtained} / {r.total}</span></> }
          ]}
          rows={s.topScorers}
          rowKey="id"
          emptyMessage="No marks entered yet."
        />
        <TablePanel
          title={<>Not Marked on {formatDate(s.lastSunday)}</>}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Section', render: (r) => <><strong>{r.label}</strong><br /><span style={sub}>{r.teachers || 'No teacher'}</span></> },
            { key: 'students', header: 'Students', align: 'center' },
            { key: 'lastRegister', header: 'Last Marked', nowrap: true, render: (r) => (r.lastRegister ? formatDate(r.lastRegister) : <span style={sub}>never</span>) }
          ]}
          rows={s.notMarked}
          rowKey="id"
          emptyMessage="Every section with students was marked."
        />
      </SummaryColumns>

      <TablePanel
        title={showAllStudents ? 'Student Attendance' : `Students Below ${LOW_ATTENDANCE_PCT}% Attendance`}
        actions={(
          <Button size="sm" variant="ghost" onClick={() => setShowAllStudents((v) => !v)}>
            {showAllStudents ? 'Show only low attendance' : 'Show every student'}
          </Button>
        )}
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="640px"
        columns={[
          { key: 'name', header: 'Student', render: (r) => <><strong>{r.name}</strong><br /><span style={sub}>{r.id} · {r.className}</span></> },
          { key: 'attendance', header: 'Attendance', align: 'center', render: (r) => <>{pctBadge(r.attendance.pct)}<br /><span style={sub}>{r.attendance.present} of {r.attendance.marked} Sundays</span></> },
          // The parent's number is here so a follow-up call needs no second screen.
          { key: 'parentName', header: 'Parent / Guardian', render: (r) => <>{r.parentName || '-'}<br /><span style={sub}>{r.phone || ''}</span></> }
        ]}
        rows={showAllStudents ? s.byStudent : s.lowAttendance}
        rowKey="id"
        emptyMessage={showAllStudents ? 'No attendance marked yet.' : `Nobody is below ${LOW_ATTENDANCE_PCT}%.`}
      />

      {s.sectionsWithoutTeacher.length > 0 && (
        <TablePanel
          title="Sections Without a Teacher"
          isDarkMode={isDarkMode}
          columns={[
            { key: 'label', header: 'Section', render: (r) => <strong>{r.label}</strong> },
            { key: 'gradeName', header: 'Grade' },
            { key: 'students', header: 'Students', align: 'center' }
          ]}
          rows={s.sectionsWithoutTeacher}
          rowKey="id"
        />
      )}

      {unassigned && unassigned.length > 0 && (
        <TablePanel
          title="Students Without a Section"
          isDarkMode={isDarkMode}
          columns={[
            { key: 'name', header: 'Student', render: (r) => <><strong>{r.name || '-'}</strong><br /><span style={sub}>{r.id}</span></> },
            { key: 'parentName', header: 'Parent / Guardian', render: (r) => <>{r.parentName || '-'}<br /><span style={sub}>{r.phone || ''}</span></> },
            { key: 'classId', header: 'Was In', render: (r) => (r.classId ? <Badge tone="warning">{r.classId} (deleted)</Badge> : <Badge tone="neutral">never enrolled</Badge>) }
          ]}
          rows={unassigned}
          rowKey={(r) => String(r.id)}
        />
      )}
    </>
  );
};

export default SundaySchoolSummaryView;
